import { debounce, isEqual, pick } from 'radash';

import { publicConfig } from '@public-config';

import {
    CompanyProfile,
    Component,
    ComponentQuery,
    ComponentSearchData,
    ComponentSearchScore,
    ComponentType,
    FilterSuggestion,
    PickedSuggestion,
    SearchHit,
} from 'models';
import { ComponentHelpers } from 'helpers/ComponentHelpers';

import { componentSearchBar as componentSearchBarState } from 'components/component-overview/state/component-search-bar';
import {
    componentSearchData as componentSearchDataState,
    DEFAULT_COMPONENT_SEARCH_DATA,
} from 'components/component-overview/state/component-search-data';
import {
    componentSearchFilters as componentSearchFiltersState,
    DEFAULT_SEARCH_COMPONENT_FILTERS,
} from 'components/component-overview/state/component-search-filters';
import {
    componentSearchWeights as componentSearchWeightsState,
    DEFAULT_COMPONENT_SEARCH_WEIGHTS,
} from 'components/component-overview/state/component-search-weights';
import {
    getConverterCategory,
    getConverterCategoryDefaultQuery,
} from 'components/dc-microgrids/sections/converter-categories';
import { getFacetKeysForType } from 'components/dc-microgrids/sections/filter-presets';
import { ApiService } from 'services/ApiService';
import { ComponentService } from 'services/ComponentService';
import { InternalTrackingService } from 'services/InternalTrackingService';

// Only the most recently started search may update the results
let latestSearchId = 0;

const COMPONENT_INSTANCE_FILTER_KEYS = [
    'type',
    'ports',
    'breakerType',
    'catalog',
    'priorityManufacturer',
    'distributor',
    'productsForCompatibility',
] as const satisfies readonly (keyof ComponentQuery)[];

const SearchService = {
    pickedSuggestion: undefined as PickedSuggestion | undefined,

    setFilter<FilterKey extends keyof ComponentQuery>(key: FilterKey, value: ComponentQuery[FilterKey]) {
        if (key === 'type' && value !== undefined && !ComponentType.validator.safeParse(value).success) {
            SearchService.setFilter('type', undefined);
            return;
        }

        componentSearchFiltersState[key] = value;
    },

    mergeFilters(filters: Partial<ComponentQuery>) {
        Object.entries(filters).forEach(([key, value]) => {
            SearchService.setFilter(key as keyof ComponentQuery, value);
        });
    },

    pickSuggestion({ filter, count }: FilterSuggestion, position: number) {
        InternalTrackingService.track('product.search.suggestion.click', {
            query: componentSearchDataState.request?.query,
            filter,
            count,
            position,
        });

        SearchService.pickedSuggestion = { filter, count, position };
        SearchService.mergeFilters(
            filter.ratings
                ? { ...filter, ratings: { ...componentSearchFiltersState.ratings, ...filter.ratings } }
                : filter,
        );
    },

    resetFilters(componentInstanceDefaults: Partial<ComponentQuery> = {}) {
        SearchService.mergeFilters({
            ...DEFAULT_SEARCH_COMPONENT_FILTERS,
            ...pick(componentInstanceDefaults, [...COMPONENT_INSTANCE_FILTER_KEYS]),
        });
    },

    getCleanFilters() {
        return { ...componentSearchFiltersState };
    },

    setSearchData<DataKey extends keyof ComponentSearchData>(key: DataKey, value: ComponentSearchData[DataKey]) {
        componentSearchDataState[key] = value;
    },

    setPage(page: number) {
        componentSearchDataState.page = page;
    },

    resetSearchData() {
        Object.keys(DEFAULT_COMPONENT_SEARCH_DATA).forEach((key) => {
            const dataKey = key as keyof ComponentSearchData;
            const dataValue = DEFAULT_COMPONENT_SEARCH_DATA[dataKey];

            SearchService.setSearchData(dataKey, dataValue);
        });
    },

    resetSearch() {
        SearchService.resetFilters();
        SearchService.resetSearchData();
        SearchService.resetSearchWeights();
    },

    setSearchWeight(weight: ComponentSearchScore, value: number) {
        componentSearchWeightsState[weight] = value;
    },

    resetSearchWeights() {
        for (const score of Object.keys(componentSearchWeightsState)) {
            delete componentSearchWeightsState[score as keyof typeof componentSearchWeightsState];
        }

        for (const [score, value] of Object.entries(DEFAULT_COMPONENT_SEARCH_WEIGHTS)) {
            SearchService.setSearchWeight(score as ComponentSearchScore, value);
        }
    },

    debouncedSearch: debounce({ delay: 500 }, () => {
        SearchService.search().then();
    }),

    search: async () => {
        const start = new Date().getTime();
        const searchId = ++latestSearchId;
        const isStale = () => searchId !== latestSearchId;

        // cancel previous search calls
        if (componentSearchDataState.isLoading) {
            ComponentService.controller?.abort();
        }

        const cleanFilters = SearchService.getCleanFilters();
        const isInitialSearch = SearchService.isInitialSearch(cleanFilters);

        const request = {
            query: cleanFilters.search ?? '',
            filters: cleanFilters,
            suggestion: componentSearchDataState.page
                ? componentSearchDataState.request?.suggestion
                : SearchService.pickedSuggestion,
        };
        SearchService.pickedSuggestion = undefined;

        if (!isInitialSearch) {
            InternalTrackingService.track('product.search', {
                ...cleanFilters,
                query: request.query,
            });
        }

        componentSearchDataState.isLoading = true;

        try {
            let result = null;

            if (isInitialSearch) {
                const featuredProducts = await ComponentService.getFeaturedProducts();

                result = {
                    docs: featuredProducts,
                    totalPages: 1,
                    totalResults: featuredProducts.length,
                    facets: undefined,
                    suggestions: undefined,
                };
            } else {
                result = await ComponentService.search(
                    {
                        ...cleanFilters,
                        featuredSubcategory: SearchService.getFeaturedSubcategory(cleanFilters),
                        customWeights: componentSearchWeightsState,
                        facets: getFacetKeysForType(cleanFilters.type),
                    },
                    componentSearchDataState.page,
                );
            }

            if (isStale()) return;

            componentSearchDataState.hasSearched = true;
            componentSearchDataState.docs = result.docs;
            componentSearchDataState.facets = result.facets;
            componentSearchDataState.suggestions = result.suggestions;
            componentSearchDataState.request = request;
            componentSearchDataState.exact = result.exact ?? true;
            componentSearchDataState.makers = result.makers;

            if (!isInitialSearch) {
                componentSearchDataState.totalComponents = result.totalResults;
            }

            componentSearchDataState.totalPages = result.totalPages;
            componentSearchDataState.isLoading = false;

            if (!isInitialSearch) {
                const scores: number[] = [];
                const parts: {
                    [key: string]: number[];
                } = {};

                result.docs.forEach((doc: any) => {
                    scores.push(doc.aggregateScore || 0);

                    Object.entries(doc.scores || {}).forEach(([score, value]) => {
                        parts[score] = parts[score] || [];
                        parts[score].push((value as number) || 0);
                    });
                });

                const totalScore = scores.reduce((a, b) => a + b, 0);
                const minimumScore = Math.min(...scores);
                const maximumScore = Math.max(...scores);
                const averageScore = totalScore / scores.length;

                const averageParts = Object.entries(parts).reduce(
                    (acc, [key, value]) => {
                        acc[key] = value.reduce((a, b) => a + b, 0) / value.length;

                        return acc;
                    },
                    {} as { [key: string]: number },
                );

                const prepareResultsForTracking = (results: any[]) => {
                    return JSON.parse(JSON.stringify(results)).map((result: any) => {
                        return pick(result, [
                            '_id',
                            '__position',
                            'type',
                            'name',
                            'productIdentifier',
                            'productSeries',
                            'aggregateScore',
                            'scores',
                            'match',
                        ]);
                    });
                };

                InternalTrackingService.track('product.search.result', {
                    ...request,
                    results: prepareResultsForTracking(result.docs),
                    page: componentSearchDataState.page + 1,
                    totalPages: componentSearchDataState.totalPages,
                    totalResults: componentSearchDataState.totalComponents,
                    exact: result.exact,
                    scores: {
                        total: totalScore,
                        minimum: minimumScore,
                        maximum: maximumScore,
                        average: averageScore,
                        parts: averageParts,
                    },
                    duration: new Date().getTime() - start,
                });
            }
        } catch (error: any) {
            if (error?.name === 'AbortError' || isStale()) {
                return;
            }

            componentSearchDataState.hasSearched = true;
            componentSearchDataState.docs = [];
            componentSearchDataState.suggestions = undefined;
            componentSearchDataState.makers = undefined;
            componentSearchDataState.error = true;
            componentSearchDataState.isLoading = false;
        }
    },

    setSearchBarQuery(query: string) {
        componentSearchBarState.query = query;
    },

    getSearchBarQuery() {
        return componentSearchBarState.query;
    },

    clearSearchBarQuery() {
        componentSearchBarState.query = '';
    },

    resetHasSearched() {
        componentSearchDataState.hasSearched = false;
    },

    isInitialSearch: (filters: ComponentQuery) => {
        return isEqual(
            ComponentHelpers.getSearchRequest(filters),
            ComponentHelpers.getSearchRequest(DEFAULT_SEARCH_COMPONENT_FILTERS),
        );
    },

    getFeaturedSubcategory: (filters: ComponentQuery): ComponentQuery['featuredSubcategory'] => {
        if (filters.catalog !== 'dc' || !filters.type) {
            return undefined;
        }

        const expected: ComponentQuery = {
            ...DEFAULT_SEARCH_COMPONENT_FILTERS,
            catalog: 'dc',
            type: filters.type,
        };

        const converterCategory =
            filters.type === 'converter' ? getConverterCategory(filters.ports, filters.maxPorts) : undefined;

        if (converterCategory) {
            const { ports, maxPorts, excludePorts } = getConverterCategoryDefaultQuery(converterCategory);
            expected.ports = ports;
            expected.maxPorts = maxPorts;
            expected.excludePorts = excludePorts;
        }

        if (!isEqual(ComponentHelpers.getSearchRequest(expected), ComponentHelpers.getSearchRequest(filters))) {
            return undefined;
        }

        return converterCategory ?? filters.type;
    },

    getAutosuggest: async (query: string) => {
        return ApiService.get<{
            companies: SearchHit<CompanyProfile>[];
            products: SearchHit<Component>[];
        }>(`${publicConfig.urls.api}/search-autosuggest?search=${encodeURIComponent(query)}`);
    },

    scrollToContent: () => {
        const content = document.querySelector('#search-content');

        if (content) {
            const { top } = content.getBoundingClientRect();

            window.scrollTo({ top: top + scrollY, behavior: 'instant' });
        }
    },
};

export { SearchService };
